import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, AlertTriangle, Sparkles, ExternalLink, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatYMD } from "@/lib/dates";

const EXTENSION_TIERS = [
  { months: 1, price: 100, label: "1 month" },
  { months: 2, price: 300, label: "2 months" },
  { months: 3, price: 500, label: "3 months" },
];

/**
 * Displays each of the user's LMS enrollments with their 5-month access
 * countdown (or "lifetime" for pre-expiry-model enrollments) and lets them
 * self-serve an extension purchase via Stripe.
 *
 * Consumed by Dashboard.jsx.
 */
export default function MyCourseAccess() {
  const [enrollments, setEnrollments] = useState(null);
  const [busyEnrollmentId, setBusyEnrollmentId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/lms/access/mine");
        if (!cancelled) setEnrollments(data);
      } catch {
        if (!cancelled) setEnrollments([]);
      }
    })();
    // If we've just returned from a successful extension checkout, celebrate + refresh
    const params = new URLSearchParams(window.location.search);
    if (params.get("extension") === "success") {
      toast.success("Extension applied — your access has been extended.");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("extension") === "cancelled") {
      toast.info("Extension checkout cancelled — no charge made.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    return () => { cancelled = true; };
  }, []);

  if (enrollments === null) return null;
  if (enrollments.length === 0) return null;

  return (
    <section className="mb-12" data-testid="my-course-access">
      <h2 className="font-serif text-3xl text-aviana-ink mb-6 flex items-center gap-3">
        <Clock size={22} className="text-aviana-gold" /> My course access
      </h2>
      <div className="space-y-4">
        {enrollments.map((e) => (
          <AccessRow
            key={e.enrollment_id}
            enr={e}
            busy={busyEnrollmentId === e.enrollment_id}
            onExtend={async (months) => {
              setBusyEnrollmentId(e.enrollment_id);
              try {
                const { data } = await api.post("/lms/extensions/checkout", {
                  enrollment_id: e.enrollment_id,
                  months,
                  origin_url: window.location.origin,
                });
                window.location.assign(data.url);
              } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail) || "Couldn't start extension checkout.");
              } finally {
                setBusyEnrollmentId(null);
              }
            }}
          />
        ))}
      </div>
    </section>
  );
}

function AccessRow({ enr, busy, onExtend }) {
  const [showTiers, setShowTiers] = useState(false);

  const state =
    !enr.has_expiry ? "lifetime" :
    enr.is_expired ? "expired" :
    enr.days_remaining <= 30 ? "urgent" : "ok";

  const toneClass = {
    lifetime: "bg-white border-aviana-border",
    ok: "bg-white border-aviana-border",
    urgent: "bg-amber-50/70 border-amber-300/60",
    expired: "bg-red-50/70 border-red-300/60",
  }[state];

  const badge = {
    lifetime: null,
    ok: null,
    urgent: (
      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-amber-800 bg-amber-100 px-2.5 py-1 rounded-full">
        <AlertTriangle size={11} /> Expiring soon
      </span>
    ),
    expired: (
      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-red-800 bg-red-100 px-2.5 py-1 rounded-full">
        <AlertTriangle size={11} /> Access ended
      </span>
    ),
  }[state];

  const dateLine = enr.access_expires_at
    ? formatYMD(enr.access_expires_at, { month: "long", day: "numeric", year: "numeric" })
    : "Lifetime access";

  return (
    <div className={`p-6 rounded-2xl border ${toneClass}`} data-testid={`access-row-${enr.enrollment_id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h4 className="font-serif text-xl text-aviana-ink">{enr.course_title}</h4>
            {badge}
          </div>
          <p className="text-aviana-muted text-[14px] mt-2">
            {state === "lifetime" && "Lifetime access"}
            {state === "ok" && `Access through ${dateLine} · ${enr.days_remaining} days remaining`}
            {state === "urgent" && `Access ends ${dateLine} · ${enr.days_remaining} days left`}
            {state === "expired" && `Access ended ${dateLine}`}
          </p>
          {enr.extensions_granted?.length > 0 && (
            <p className="text-[12px] text-aviana-plum mt-2 flex items-center gap-1">
              <CheckCircle2 size={12} /> {enr.extensions_granted.length} extension{enr.extensions_granted.length === 1 ? "" : "s"} applied
              &nbsp;·&nbsp; {enr.extensions_remaining} remaining
            </p>
          )}
        </div>
      </div>

      {enr.can_extend && (
        <div className="mt-5 pt-5 border-t border-aviana-border">
          {!showTiers ? (
            <button
              onClick={() => setShowTiers(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-aviana-plum text-aviana-cream text-[11px] tracking-[0.18em] uppercase hover:bg-aviana-plumDeep transition-colors"
              data-testid={`request-extension-${enr.enrollment_id}`}
            >
              <Sparkles size={12} /> Request an extension
            </button>
          ) : (
            <div>
              <p className="text-[13px] text-aviana-inkSoft mb-4 leading-[1.6]">
                Choose an extension length below. Payment is processed through Stripe. Extensions apply from your original expiry date, so buying now doesn't shorten your current window. If you'd prefer to pay via Zelle or Venmo, email <a href="mailto:holly@avianayoga.com" className="text-aviana-plum underline">holly@avianayoga.com</a>.
              </p>
              <div className="grid grid-cols-3 gap-3">
                {EXTENSION_TIERS.map((t) => (
                  <button
                    key={t.months}
                    onClick={() => onExtend(t.months)}
                    disabled={busy}
                    className="p-4 rounded-2xl border border-aviana-border hover:border-aviana-plum bg-white text-left transition-colors disabled:opacity-50"
                    data-testid={`extend-${t.months}mo-${enr.enrollment_id}`}
                  >
                    <p className="font-figure text-2xl text-aviana-ink">${t.price}</p>
                    <p className="text-[12px] tracking-[0.12em] uppercase text-aviana-muted mt-1">{t.label}</p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowTiers(false)}
                className="mt-3 text-[11px] tracking-[0.18em] uppercase text-aviana-muted hover:text-aviana-ink"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {!enr.can_extend && enr.has_expiry && enr.extensions_remaining === 0 && (
        <p className="mt-5 pt-5 border-t border-aviana-border text-[13px] text-aviana-inkSoft leading-[1.6]">
          You've used the maximum of 2 extensions on this enrollment. To complete your certification you'll need to re-attend a teacher training intensive. Reach out to <a href="mailto:holly@avianayoga.com" className="text-aviana-plum underline">holly@avianayoga.com</a> to discuss options.
        </p>
      )}
    </div>
  );
}
